// src/App.js
import React from "react";
import { ProductBrainProvider, useProductBrain } from "./contexts/ProductBrainContext";
import { GuideProvider, useGuide } from "./contexts/GuideContext";
import Onboarding from "./pages/Onboarding";
import Welcome from "./pages/Welcome";
import CommandCenter from "./pages/CommandCenterOverview";
import ProductBrain from "./pages/ProductBrain";
import AskSPCC from "./pages/AskSPCC";
import Inbox from "./pages/Inbox";
import Decisions from "./pages/Decisions";
import Priorities from "./pages/Priorities";
import LearningWorkspace from "./pages/LearningWorkspace";
import ReminderRunner from "./components/brain/ReminderRunner";
import AutopilotRunner from "./components/brain/AutopilotRunner";
import BackgroundJobs from "./components/ui/background-jobs";
import Autopilot from "./pages/Autopilot";
import Meetings from "./pages/Meetings";
import TicketWriter from "./pages/TicketWriter";
import "./v2.css";
import "./styles/interactions.css";
import ActionFeedback from "./components/ui/ActionFeedback";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./Layout";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginForm from "./components/auth/LoginForm";
import CalendarAuthCallback from "./components/auth/CalendarAuthCallback";

// PAGES
import Dashboard from "./pages/Dashboard";
import Discovery from "./pages/Discovery";
import Roadmap from "./pages/Roadmap";
import OKRs from "./pages/OKRs";
import Firefighter from "./pages/Firefighter";
import TimeAudit from "./pages/TimeAudit";
import Reminders from "./pages/Reminders";
import OGPreviewHealthCheck from "pages/OGPreviewHealthCheck";
import LogoLoader from "./components/ui/logo-loader";

// Protected route wrapper
// First visit to the home page opens the welcome flow; legacy and started workspaces go straight in.
// The guide's optimistic activation counts too: the workspace reloads only after a short debounce.
export function firstRunTarget(pathname, workspace, activation = null) {
  if (pathname !== '/' || !workspace) return null;
  return workspace.onboarding?.status || workspace.onboarding?.activation?.started_at || activation?.started_at ? null : '/welcome';
}

// Background reloads (every saved record triggers one) must not unmount the page the user is on.
export const blocksOnWorkspace = (pathname, loading, workspace) => pathname === '/' && loading && !workspace;

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const { workspace, loading: workspaceLoading } = useProductBrain();
  const guide = useGuide();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <LogoLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  if (blocksOnWorkspace(location.pathname, workspaceLoading, workspace)) return <div className="loading-screen"><LogoLoader label="Loading your workspace…" /></div>;
  const target = firstRunTarget(location.pathname, workspace, guide?.activation);
  if (target) return <Navigate to={target} replace/>;

  return children;
}

// App routes component
function AppRoutes() {
  return (
    <Routes>
      <Route path="/autopilot" element={<ProtectedRoute><Layout><Autopilot /></Layout></ProtectedRoute>} />
      <Route path="/meetings" element={<ProtectedRoute><Layout><Meetings /></Layout></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><Layout><TicketWriter /></Layout></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Layout><Onboarding /></Layout></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Layout><Welcome /></Layout></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout><CommandCenter /></Layout></ProtectedRoute>} />
      <Route path="/overview" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/brain" element={<ProtectedRoute><Layout><ProductBrain /></Layout></ProtectedRoute>} />
      <Route path="/ask" element={<ProtectedRoute><Layout><AskSPCC /></Layout></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><Layout><Inbox /></Layout></ProtectedRoute>} />
      <Route path="/priorities" element={<ProtectedRoute><Layout><Priorities /></Layout></ProtectedRoute>} />
      <Route path="/decisions" element={<ProtectedRoute><Layout><Decisions /></Layout></ProtectedRoute>} />
      <Route path="/learning" element={<ProtectedRoute><Layout><LearningWorkspace /></Layout></ProtectedRoute>} />
      <Route path="/discovery" element={<ProtectedRoute><Layout><Discovery /></Layout></ProtectedRoute>} />
      <Route path="/roadmap" element={<ProtectedRoute><Layout><Roadmap /></Layout></ProtectedRoute>} />
      <Route path="/okrs" element={<ProtectedRoute><Layout><OKRs /></Layout></ProtectedRoute>} />
      <Route path="/issues" element={<ProtectedRoute><Layout><Firefighter /></Layout></ProtectedRoute>} />
      <Route path="/time" element={<ProtectedRoute><Layout><TimeAudit /></Layout></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><Layout><Reminders /></Layout></ProtectedRoute>} />
      <Route path="/og-check" element={<OGPreviewHealthCheck />} />
      <Route path="/auth/microsoft/callback" element={<CalendarAuthCallback />} />
      <Route path="*" element={<ProtectedRoute><Layout><div className="empty"><strong>Page not found</strong><span>The page you’re looking for doesn’t exist.</span></div></Layout></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ProductBrainProvider>
        <ReminderRunner />
        <AutopilotRunner />
        <BackgroundJobs />
        <ActionFeedback />
        <GuideProvider><AppRoutes /></GuideProvider>
      </ProductBrainProvider>
    </AuthProvider>
  );
}
